import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  ClipboardCheck,
  Copy,
  ExternalLink,
  KeyRound,
  Network,
  Server,
  Settings,
  ShieldCheck,
  Terminal,
  Wrench,
  X,
} from 'lucide-react';

interface ZabbixAgent2InstallationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'vue' | 'installation' | 'tls' | 'zabbix' | 'hermes' | 'validation' | 'depannage' | 'bot';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'vue', label: 'Vue d’ensemble', icon: Server },
  { id: 'installation', label: 'Installation', icon: Terminal },
  { id: 'tls', label: 'TLS-PSK', icon: KeyRound },
  { id: 'zabbix', label: 'Interface Zabbix', icon: Settings },
  { id: 'hermes', label: 'Compte Hermes', icon: Bot },
  { id: 'validation', label: 'Validation', icon: ClipboardCheck },
  { id: 'depannage', label: 'Dépannage', icon: Wrench },
  { id: 'bot', label: 'Contrat bot', icon: Bot },
];

function Panel({ title, icon: Icon, children, tone = 'cyan' }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  tone?: 'cyan' | 'amber' | 'emerald' | 'rose';
}) {
  const tones = {
    cyan: 'border-cyan-300/20 bg-cyan-400/5 text-cyan-200',
    amber: 'border-amber-300/25 bg-amber-400/10 text-amber-200',
    emerald: 'border-emerald-300/20 bg-emerald-400/5 text-emerald-200',
    rose: 'border-rose-300/25 bg-rose-400/10 text-rose-200',
  };
  return (
    <section className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <h3 className="mb-4 flex items-center gap-2 text-lg font-black text-white">
        <Icon className="h-5 w-5" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function Command({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopyFailed(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopyFailed(true);
    }
  };
  return (
    <div className="group relative my-3 overflow-hidden rounded-xl border border-slate-700 bg-slate-950">
      <pre className="overflow-x-auto p-4 pr-12 text-xs leading-6 text-emerald-300"><code>{children}</code></pre>
      <button type="button" onClick={() => void copy()} className="absolute right-2 top-2 rounded-lg border border-slate-600 bg-slate-800 p-2 text-slate-300 hover:bg-slate-700" title="Copier la commande">
        {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      </button>
      <span className="sr-only" aria-live="polite">{copied ? 'Commande copiée.' : copyFailed ? 'La copie a échoué.' : ''}</span>
    </div>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 text-sm leading-6 text-slate-300">
      {items.map((item) => <li key={item} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" /><span>{item}</span></li>)}
    </ul>
  );
}

function VueTab() {
  return <div className="space-y-5">
    <Panel title="Standard interne BZ" icon={ShieldCheck}>
      <div className="grid gap-3 md:grid-cols-2">
        {[
          ['Version', 'Zabbix Agent 2 7.4'],
          ['Mode', 'Contrôles actifs'],
          ['Serveur actif', 'zab03.bzhosting.ca:10051'],
          ['Sécurité', 'TLS-PSK — une PSK unique par machine'],
          ['Groupe', 'AGENT_BZ'],
          ['Modèle', 'Linux by Zabbix agent active'],
          ['Tag host', 'bots=host si le hostname contient « host »'],
          ['Tag agent', 'bots=agent dans les autres cas'],
        ].map(([label, value]) => <div key={label} className="rounded-xl border border-white/10 bg-black/20 p-4"><p className="text-xs font-bold uppercase tracking-wider text-cyan-300">{label}</p><p className="mt-1 text-sm font-semibold text-white">{value}</p></div>)}
      </div>
    </Panel>
    <Panel title="Règles de sécurité non négociables" icon={AlertTriangle} tone="rose">
      <Bullets items={[
        'Ne jamais réutiliser la PSK d’une autre machine.',
        'La PSK ne doit jamais être copiée dans PRIsME, Git, un document, un journal ou une conversation.',
        'Un bot ne doit jamais lire, afficher, transmettre ni conserver la valeur de la PSK.',
        'Seule la PSK est protégée en 0600 zabbix:zabbix; le fichier de configuration non secret reste en 0644 root:root.',
        'Le port 10050 ne doit normalement pas être exposé à Internet : la machine initie la connexion active vers TCP 10051.',
      ]} />
    </Panel>
    <Panel title="Périmètre supporté" icon={Server}>
      <p className="text-sm leading-6 text-slate-300">Procédure validée pour Ubuntu 24.04, Ubuntu 22.04 et Debian 12. Toujours confirmer la distribution, la version et l’architecture avant de télécharger un paquet.</p>
    </Panel>
  </div>;
}

function InstallationTab() {
  return <div className="space-y-5">
    <Panel title="1 — Vérifier l’identité du serveur" icon={Server}>
      <Command>{`hostnamectl --static
cat /etc/os-release
uname -m`}</Command>
      <p className="text-sm leading-6 text-slate-300">Le hostname doit être unique et correspondre exactement au champ <strong className="text-white">Host name</strong> dans Zabbix.</p>
      <Command>{`sudo hostnamectl set-hostname NOM_UNIQUE_DU_SERVEUR`}</Command>
      <p className="text-xs text-amber-200">Après un changement, reconnecter la session SSH ou ouvrir un nouveau shell.</p>
    </Panel>
    <Panel title="2 — Installer le dépôt officiel Zabbix 7.4" icon={Terminal}>
      <p className="font-bold text-white">Ubuntu 24.04</p>
      <Command>{`cd /tmp
wget https://repo.zabbix.com/zabbix/7.4/release/ubuntu/pool/main/z/zabbix-release/zabbix-release_latest_7.4+ubuntu24.04_all.deb
sudo dpkg -i zabbix-release_latest_7.4+ubuntu24.04_all.deb
sudo apt-get update`}</Command>
      <p className="font-bold text-white">Ubuntu 22.04</p>
      <Command>{`cd /tmp
wget https://repo.zabbix.com/zabbix/7.4/release/ubuntu/pool/main/z/zabbix-release/zabbix-release_latest_7.4+ubuntu22.04_all.deb
sudo dpkg -i zabbix-release_latest_7.4+ubuntu22.04_all.deb
sudo apt-get update`}</Command>
      <p className="font-bold text-white">Debian 12</p>
      <Command>{`cd /tmp
wget https://repo.zabbix.com/zabbix/7.4/release/debian/pool/main/z/zabbix-release/zabbix-release_latest_7.4+debian12_all.deb
sudo dpkg -i zabbix-release_latest_7.4+debian12_all.deb
sudo apt-get update`}</Command>
    </Panel>
    <Panel title="3 — Installer uniquement l’agent requis" icon={Terminal}>
      <Command>{`sudo apt-get install -y zabbix-agent2
zabbix_agent2 -V`}</Command>
      <div className="rounded-xl border border-amber-300/20 bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">
        Ne jamais installer les extensions avec <code>zabbix-agent2-plugin-*</code>. Une extension non requise, notamment NVIDIA, peut empêcher le service de démarrer.
      </div>
    </Panel>
  </div>;
}

function TlsTab() {
  return <div className="space-y-5">
    <Panel title="4 — Générer une PSK distincte de 256 bits" icon={KeyRound}>
      <Command>{`sudo openssl rand -hex 32 | sudo tee /etc/zabbix/zabbix_agent2.psk >/dev/null
sudo chown zabbix:zabbix /etc/zabbix/zabbix_agent2.psk
sudo chmod 600 /etc/zabbix/zabbix_agent2.psk`}</Command>
      <p className="text-sm text-slate-300">Vérifier uniquement les permissions, sans afficher la PSK :</p>
      <Command>{`sudo stat -c '%A %U:%G %n' /etc/zabbix/zabbix_agent2.psk`}</Command>
      <p className="rounded-xl bg-black/20 p-3 font-mono text-xs text-emerald-300">Résultat attendu : -rw------- zabbix:zabbix /etc/zabbix/zabbix_agent2.psk</p>
      <div className="mt-4 rounded-xl border border-rose-300/25 bg-rose-400/10 p-4 text-sm leading-6 text-rose-100">
        La valeur peut être consultée par un humain uniquement dans un terminal privé au moment de configurer Zabbix. Un bot doit s’arrêter et demander cette saisie humaine; il ne doit pas exécuter une commande qui affiche la PSK.
      </div>
    </Panel>
    <Panel title="5 — Configurer les contrôles actifs et TLS" icon={Network}>
      <p className="text-sm text-slate-300">Créer <code>/etc/zabbix/zabbix_agent2.d/active-psk.conf</code> avec le hostname exact :</p>
      <Command>{`sudo tee /etc/zabbix/zabbix_agent2.d/active-psk.conf >/dev/null <<'EOF'
ServerActive=zab03.bzhosting.ca:10051
Hostname=NOM_UNIQUE_DU_SERVEUR
TLSConnect=psk
TLSAccept=psk
TLSPSKIdentity=PSK-NOM_UNIQUE_DU_SERVEUR
TLSPSKFile=/etc/zabbix/zabbix_agent2.psk
EOF`}</Command>
      <Command>{`sudo chown root:root /etc/zabbix/zabbix_agent2.d/active-psk.conf
sudo chmod 644 /etc/zabbix/zabbix_agent2.d/active-psk.conf`}</Command>
      <p className="text-sm text-slate-300">Chercher les paramètres en double :</p>
      <Command>{`sudo grep -RniE '^[[:space:]]*(ServerActive|Hostname|TLSConnect|TLSAccept|TLSPSKIdentity|TLSPSKFile)=' \
  /etc/zabbix/zabbix_agent2.conf /etc/zabbix/zabbix_agent2.d/`}</Command>
      <p className="text-xs text-amber-200">Chaque paramètre doit avoir une seule définition active. Commenter ou retirer les anciennes définitions redondantes.</p>
    </Panel>
    <Panel title="6 — Valider puis démarrer" icon={CheckCircle2} tone="emerald">
      <Command>{`sudo runuser -u zabbix -- zabbix_agent2 -T -c /etc/zabbix/zabbix_agent2.conf`}</Command>
      <p className="text-sm text-slate-300">Seulement si la validation réussit :</p>
      <Command>{`sudo systemctl enable --now zabbix-agent2
sudo systemctl restart zabbix-agent2
systemctl is-enabled zabbix-agent2
systemctl is-active zabbix-agent2
sudo systemctl status zabbix-agent2 --no-pager`}</Command>
      <Command>{`sudo journalctl -u zabbix-agent2 --since '10 minutes ago' --no-pager`}</Command>
    </Panel>
  </div>;
}

function ZabbixTab() {
  return <div className="space-y-5">
    <Panel title="7 — Tester l’accès sortant" icon={Network}>
      <Command>{`nc -vz zab03.bzhosting.ca 10051`}</Command>
      <p className="text-sm text-slate-300">Si netcat est absent :</p>
      <Command>{`sudo apt-get install -y netcat-openbsd
nc -vz zab03.bzhosting.ca 10051`}</Command>
      <p className="text-sm text-cyan-100">En mode exclusivement actif, il n’est normalement pas nécessaire d’exposer TCP 10050 vers Internet.</p>
    </Panel>
    <Panel title="8 — Créer l’hôte dans Zabbix" icon={Settings}>
      <p className="mb-3 text-sm text-slate-300">Dans <strong className="text-white">Data collection → Hosts</strong> :</p>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm"><tbody>
          {[
            ['Host name', 'Résultat exact de hostnamectl --static'],
            ['Host groups', 'AGENT_BZ'],
            ['Templates', 'Linux by Zabbix agent active'],
            ['Connections to host', 'PSK'],
            ['Connections from host', 'PSK'],
            ['PSK identity', 'PSK-NOM_UNIQUE_DU_SERVEUR'],
            ['PSK', 'Contenu du fichier local — saisie humaine privée'],
          ].map(([field, value]) => <tr key={field} className="border-b border-white/10 last:border-0"><th className="bg-white/5 px-4 py-3 text-left text-cyan-200">{field}</th><td className="px-4 py-3 text-slate-300">{value}</td></tr>)}
        </tbody></table>
      </div>
      <div className="mt-4 rounded-xl border border-amber-300/20 bg-amber-400/10 p-4 text-sm text-amber-100">L’identité PSK doit être strictement identique dans Zabbix et dans <code>active-psk.conf</code>.</div>
    </Panel>
    <Panel title="Tag de classification" icon={Bot}>
      <Bullets items={['Hostname contenant « host » : bots=host.', 'Tous les autres hostnames : bots=agent.']} />
    </Panel>
  </div>;
}

function HermesTab() {
  return <div className="space-y-5">
    <Panel title="9 — Détecter Hermes avant de créer l’item" icon={Bot} tone="amber">
      <p className="text-sm leading-6 text-slate-200">Cette intégration s’applique uniquement aux machines où Hermes Agent est réellement installé. Avant toute modification, consigner l’identité de la machine et détecter au moins un fichier d’authentification Hermes.</p>
      <Command>{`hostnamectl --static
cat /etc/machine-id
systemd-detect-virt
findmnt -T /etc
findmnt -T /usr
systemctl show zabbix-agent2 -p NoNewPrivileges --value
sudo find /root/.hermes /home/*/.hermes -type f -name auth.json -print 2>/dev/null`}</Command>
      <div className="rounded-xl border border-rose-300/25 bg-rose-400/10 p-4 text-sm leading-6 text-rose-100">Si aucun fichier <code>auth.json</code> Hermes n’existe, ne déployer aucun helper et ne créer aucun item <code>app.hermes.openai.email</code>.</div>
    </Panel>

    <Panel title="10 — Installer le helper d’extraction local" icon={ShieldCheck}>
      <p className="text-sm leading-6 text-slate-300">Le helper lit tous les emplacements supportés, cherche récursivement les champs <code>access_token</code>, décode seulement le payload JWT en Base64URL et ne retourne que les courriels dont <code>profile.email_verified</code> vaut strictement <code>true</code>. Il ne valide pas cryptographiquement le JWT et n’affiche jamais un jeton.</p>
      <Command>{`sudo tee /usr/local/sbin/zabbix-hermes-account-email >/dev/null <<'PY'
#!/usr/bin/env python3
import base64
import glob
import json
import re

AUTH_GLOBS = (
    "/root/.hermes/auth.json",
    "/root/.hermes/profiles/*/auth.json",
    "/home/*/.hermes/auth.json",
    "/home/*/.hermes/profiles/*/auth.json",
)
EMAIL_KEY = "https://api.openai.com/profile.email"
VERIFIED_KEY = "https://api.openai.com/profile.email_verified"
EMAIL_RE = re.compile(r"^[A-Za-z0-9.!#$%&'*+/=?^_\x60{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$")

def tokens(value):
    if isinstance(value, dict):
        for key, child in value.items():
            if key == "access_token" and isinstance(child, str):
                yield child
            yield from tokens(child)
    elif isinstance(value, list):
        for child in value:
            yield from tokens(child)

emails = set()
paths = sorted({path for pattern in AUTH_GLOBS for path in glob.glob(pattern)})
for path in paths:
    try:
        with open(path, "r", encoding="utf-8") as handle:
            auth = json.load(handle)
        for token in tokens(auth):
            parts = token.split(".")
            if len(parts) != 3:
                continue
            raw = parts[1] + "=" * (-len(parts[1]) % 4)
            payload = json.loads(base64.urlsafe_b64decode(raw).decode("utf-8"))
            email = payload.get(EMAIL_KEY)
            if payload.get(VERIFIED_KEY) is True and isinstance(email, str) and EMAIL_RE.fullmatch(email):
                emails.add(email)
    except (OSError, ValueError, TypeError, UnicodeError, json.JSONDecodeError):
        continue

print(", ".join(sorted(emails)) if emails else "email-unavailable")
PY
sudo chown root:root /usr/local/sbin/zabbix-hermes-account-email
sudo chmod 0755 /usr/local/sbin/zabbix-hermes-account-email`}</Command>
      <Bullets items={[
        'Sortie permise : un courriel vérifié, une liste triée séparée par « , », ou email-unavailable.',
        'Sortie interdite : access_token, refresh_token, JWT complet, contenu de auth.json, PSK ou tout autre secret.',
        'Une absence de fichier, un JSON invalide ou un jeton inutilisable doit produire email-unavailable sans faire échouer l’item.',
      ]} />
    </Panel>

    <Panel title="11 — Choisir le mécanisme selon le durcissement" icon={Settings}>
      <p className="font-bold text-white">Chemin standard — élévation sudo permise</p>
      <Command>{`echo 'zabbix ALL=(root) NOPASSWD: /usr/local/sbin/zabbix-hermes-account-email' | sudo tee /etc/sudoers.d/zabbix-hermes-account-email >/dev/null
sudo chown root:root /etc/sudoers.d/zabbix-hermes-account-email
sudo chmod 0440 /etc/sudoers.d/zabbix-hermes-account-email
sudo visudo -cf /etc/sudoers.d/zabbix-hermes-account-email
echo 'UserParameter=app.hermes.openai.email,sudo -n /usr/local/sbin/zabbix-hermes-account-email' | sudo tee /etc/zabbix/zabbix_agent2.d/93-hermes-account-email.conf >/dev/null`}</Command>
      <Command>{`sudo chown root:root /etc/zabbix/zabbix_agent2.d/93-hermes-account-email.conf
sudo chmod 0644 /etc/zabbix/zabbix_agent2.d/93-hermes-account-email.conf`}</Command>
      <p className="mt-5 font-bold text-white">Chemin durci — <code>NoNewPrivileges=true</code></p>
      <p className="text-sm leading-6 text-slate-300">Ne jamais désactiver ni affaiblir <code>NoNewPrivileges</code>. Faire produire par root un fichier nettoyé et atomique, puis laisser Zabbix lire uniquement ce fichier.</p>
      <Command>{`sudo tee /usr/local/sbin/update-zabbix-hermes-account-email >/dev/null <<'SH'
#!/bin/sh
set -eu
dir=/var/lib/zabbix
install -d -o zabbix -g zabbix -m 0755 "$dir"
tmp=$(mktemp "$dir/.hermes-account-email.XXXXXX")
trap 'rm -f "$tmp"' EXIT
/usr/local/sbin/zabbix-hermes-account-email >"$tmp"
chown zabbix:zabbix "$tmp"
chmod 0644 "$tmp"
mv -f "$tmp" "$dir/hermes-account-email"
trap - EXIT
SH
sudo chown root:root /usr/local/sbin/update-zabbix-hermes-account-email
sudo chmod 0755 /usr/local/sbin/update-zabbix-hermes-account-email

sudo tee /etc/systemd/system/zabbix-hermes-account-email.service >/dev/null <<'EOF'
[Unit]
Description=Actualise le courriel GPT nettoyé de Hermes pour Zabbix

[Service]
Type=oneshot
ExecStart=/usr/local/sbin/update-zabbix-hermes-account-email
EOF

sudo tee /etc/systemd/system/zabbix-hermes-account-email.path >/dev/null <<'EOF'
[Unit]
Description=Surveille le fichier principal d’authentification Hermes

[Path]
PathChanged=/root/.hermes/auth.json
Unit=zabbix-hermes-account-email.service

[Install]
WantedBy=multi-user.target
EOF

echo 'UserParameter=app.hermes.openai.email,cat /var/lib/zabbix/hermes-account-email' | sudo tee /etc/zabbix/zabbix_agent2.d/93-hermes-account-email.conf >/dev/null
sudo chown root:root /etc/zabbix/zabbix_agent2.d/93-hermes-account-email.conf
sudo chmod 0644 /etc/zabbix/zabbix_agent2.d/93-hermes-account-email.conf
sudo systemctl daemon-reload
sudo systemctl start zabbix-hermes-account-email.service
sudo systemctl enable --now zabbix-hermes-account-email.path`}</Command>
      <Bullets items={[
        'Le fichier final doit être zabbix:zabbix 0644; le compte zabbix ne reçoit jamais accès à /root/.hermes/auth.json.',
        'Le path unit surveille /root/.hermes/auth.json; relancer aussi le service après une modification d’un auth.json de profil ou sous /home.',
        'Vérifier que le service oneshot réussit, que le path unit est actif et que NoNewPrivileges demeure actif.',
      ]} />
    </Panel>

    <Panel title="12 — Respecter les systèmes en lecture seule" icon={AlertTriangle} tone="rose">
      <Bullets items={[
        'Détecter les options réelles avec findmnt -T /etc et findmnt -T /usr avant l’installation.',
        'Ne remonter en lecture-écriture que les bind mounts requis, uniquement après autorisation explicite.',
        'Installer un trap shell qui restaure les options originales même en cas d’erreur.',
        'Restaurer immédiatement /etc et /usr en lecture seule, puis relire leurs options finales.',
        'Ne jamais terminer l’intervention en laissant /etc ou /usr en écriture.',
      ]} />
    </Panel>

    <Panel title="13 — Créer ou mettre à jour l’item par API" icon={Network}>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm"><tbody>
          {[
            ['Nom', 'Compte Hermes — courriel GPT'],
            ['Clé', 'app.hermes.openai.email'],
            ['Type', 'Zabbix agent actif'],
            ['Type de valeur', 'Texte'],
            ['Intervalle normal', '1h'],
            ['Tags', 'component=hermes-auth · data=account-email'],
            ['Valeur de repli', 'email-unavailable'],
          ].map(([field, value]) => <tr key={field} className="border-b border-white/10 last:border-0"><th className="bg-white/5 px-4 py-3 text-left text-cyan-200">{field}</th><td className="px-4 py-3 text-slate-300">{value}</td></tr>)}
        </tbody></table>
      </div>
      <Bullets items={[
        'Chercher la clé exacte sur l’hôte : zéro résultat = item.create; un résultat = item.update; plus d’un = arrêt et correction du doublon.',
        'Pour un item existant, conserver son itemid et son historique avec item.update; ne jamais le supprimer puis le recréer.',
        'Définir explicitement le type actif, le type Texte, delay=1h et les deux tags; ne pas dépendre des valeurs par défaut de l’API.',
        'Ne jamais inclure de jeton Hermes, de PSK ou de secret dans les requêtes de rapport, journaux ou artefacts.',
      ]} />
    </Panel>

    <Panel title="14 — Validation locale et preuve Zabbix" icon={ClipboardCheck} tone="emerald">
      <Command>{`sudo runuser -u zabbix -- zabbix_agent2 -t app.hermes.openai.email -c /etc/zabbix/zabbix_agent2.conf
sudo runuser -u zabbix -- zabbix_agent2 -T -c /etc/zabbix/zabbix_agent2.conf
systemctl is-active zabbix-agent2
systemctl is-enabled zabbix-agent2
sudo systemctl restart zabbix-agent2`}</Command>
      <ol className="space-y-2 text-sm leading-6 text-slate-300">
        <li>1. Par API, mettre temporairement l’item à <code>delay=1m</code>.</li>
        <li>2. Attendre légèrement plus d’un intervalle et relire l’item ainsi que sa dernière valeur.</li>
        <li>3. Exiger une valeur fraîche, <code>state=0</code> et un champ <code>error</code> vide.</li>
        <li>4. Dans un bloc <code>finally</code>, restaurer impérativement <code>delay=1h</code>, puis relire l’item pour le confirmer.</li>
        <li>5. Pour chaque hôte traité, rapporter explicitement la valeur nettoyée ou <code>email-unavailable</code>, sans secret.</li>
      </ol>
      <div className="mt-4 rounded-xl border border-amber-300/20 bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">Le livrable d’exécution doit inclure les fichiers modifiés, un diff, les validations, une preuve API d’une valeur fraîche, la confirmation que tous les intervalles sont revenus à 1h et la liste explicite des hôtes à <code>email-unavailable</code>.</div>
    </Panel>
  </div>;
}

const checklist = [
  'Hostname unique et identique dans Linux et Zabbix',
  'Zabbix Agent 2 installé depuis le dépôt officiel 7.4',
  'PSK unique de 256 bits',
  'PSK en 0600 zabbix:zabbix',
  'Configuration active/TLS validée comme utilisateur zabbix',
  'Service activé et actif',
  'TCP 10051 accessible vers zab03.bzhosting.ca',
  'Hôte placé dans AGENT_BZ',
  'Modèle Linux by Zabbix agent active',
  'Tag bots=host ou bots=agent',
  'Item de courriel GPT créé uniquement si Hermes est installé',
  'Collecte Hermes validée sans exposer de jeton ni de fichier auth.json',
  'Intervalle app.hermes.openai.email confirmé à 1h après le test',
  'Données CPU, RAM, uptime et système réellement reçues',
];

function ValidationTab() {
  return <div className="space-y-5">
    <Panel title="15 — Vérifier la réception réelle des données" icon={ClipboardCheck} tone="emerald">
      <p className="mb-3 text-sm leading-6 text-slate-300">Attendre une à deux minutes, puis ouvrir <strong className="text-white">Monitoring → Latest data</strong> et filtrer sur l’hôte. Vérifier :</p>
      <Bullets items={['Zabbix agent ping', 'Utilisation CPU', 'Utilisation RAM et quantité de RAM utilisée', 'Uptime', 'Version et informations du système d’exploitation', 'Contrôles actifs disponibles : active_available=1 via l’API Zabbix']} />
      <div className="mt-4 rounded-xl border border-rose-300/25 bg-rose-400/10 p-4 text-sm font-semibold leading-6 text-rose-100">Le service zabbix-agent2 actif ne suffit pas à confirmer que l’installation fonctionne : il faut vérifier que de vraies valeurs récentes arrivent dans Zabbix.</div>
    </Panel>
    <Panel title="Liste de contrôle finale" icon={CheckCircle2}>
      <div className="grid gap-2 md:grid-cols-2">
        {checklist.map((item) => <div key={item} className="flex gap-2 rounded-xl border border-white/10 bg-black/20 p-3 text-sm text-slate-300"><span className="mt-0.5 h-4 w-4 shrink-0 rounded border border-cyan-400" />{item}</div>)}
      </div>
    </Panel>
    <Panel title="Références officielles" icon={ExternalLink}>
      <div className="flex flex-col gap-2 text-sm">
        <a className="text-cyan-300 underline hover:text-cyan-200" href="https://www.zabbix.com/documentation/7.4/en/manual/concepts/agent" target="_blank" rel="noreferrer">Documentation Zabbix Agent 7.4</a>
        <a className="text-cyan-300 underline hover:text-cyan-200" href="https://repo.zabbix.com/zabbix/7.4/" target="_blank" rel="noreferrer">Dépôt officiel Zabbix 7.4</a>
      </div>
    </Panel>
  </div>;
}

function DepannageTab() {
  return <div className="space-y-5">
    <Panel title="16 — L’hôte reste indisponible" icon={Wrench} tone="amber">
      <Bullets items={['Comparer exactement Hostname entre Linux et Zabbix.', 'Comparer exactement TLSPSKIdentity.', 'Faire confirmer par un humain que la PSK saisie correspond au fichier local.']} />
      <Command>{`sudo journalctl -u zabbix-agent2 -n 100 --no-pager`}</Command>
    </Panel>
    <Panel title="Erreur de permission sur la PSK" icon={KeyRound}>
      <Command>{`sudo chown zabbix:zabbix /etc/zabbix/zabbix_agent2.psk
sudo chmod 600 /etc/zabbix/zabbix_agent2.psk
sudo runuser -u zabbix -- test -r /etc/zabbix/zabbix_agent2.psk`}</Command>
    </Panel>
    <Panel title="Configuration non lisible" icon={Settings}>
      <Command>{`sudo chown root:root /etc/zabbix/zabbix_agent2.d/active-psk.conf
sudo chmod 644 /etc/zabbix/zabbix_agent2.d/active-psk.conf`}</Command>
    </Panel>
    <Panel title="Extension NVIDIA installée par erreur" icon={AlertTriangle} tone="rose">
      <p className="text-sm text-slate-300">Sur une machine sans GPU NVIDIA :</p>
      <Command>{`sudo apt-get purge -y zabbix-agent2-plugin-nvidia-gpu
sudo rm -f /etc/zabbix/zabbix_agent2.d/plugins.d/nvidia.conf
sudo dpkg --configure -a
sudo systemctl restart zabbix-agent2`}</Command>
    </Panel>
  </div>;
}

function BotTab() {
  return <div className="space-y-5">
    <Panel title="Contrat d’exécution pour un bot" icon={Bot} tone="amber">
      <ol className="space-y-3 text-sm leading-6 text-slate-200">
        {[
          'Obtenir une autorisation explicite pour la machine ciblée et confirmer qu’elle est Debian 12, Ubuntu 22.04 ou Ubuntu 24.04.',
          'Lire hostnamectl, /etc/os-release et uname -m avant toute modification; ne jamais inventer le hostname, la distribution ou l’architecture.',
          'Signaler tout hostname non unique et demander validation avant de le changer.',
          'Installer seulement zabbix-agent2 et le dépôt correspondant exactement à la distribution; ne jamais utiliser zabbix-agent2-plugin-*.',
          'Créer une nouvelle PSK avec openssl rand -hex 32 pour cette machine uniquement. Ne jamais réutiliser, lire, imprimer, copier, journaliser ou transmettre la PSK.',
          'S’arrêter pour une intervention humaine privée lors de la saisie de la PSK dans l’interface Zabbix.',
          'Avant le redémarrage, détecter et résoudre les paramètres en double, puis exécuter zabbix_agent2 -T comme utilisateur zabbix.',
          'Ne démarrer ou redémarrer le service que si la validation de configuration réussit.',
          'Tester la sortie TCP 10051; ne pas ouvrir TCP 10050 vers Internet pour ce mode actif.',
          'Créer l’hôte avec AGENT_BZ, Linux by Zabbix agent active, TLS-PSK et le tag bots approprié.',
          'Détecter Hermes avant toute collecte de compte; ne jamais créer app.hermes.openai.email sur une machine sans Hermes.',
          'Extraire localement uniquement les courriels GPT vérifiés; ne jamais transmettre access_token, refresh_token, JWT, auth.json ou tout autre secret.',
          'Si NoNewPrivileges est actif, conserver ce durcissement et utiliser le fichier nettoyé produit par le service root.',
          'Créer ou mettre à jour l’item par clé exacte, préserver son historique et garantir dans un bloc finally le retour de delay à 1h.',
          'Ne déclarer la tâche réussie qu’après réception de données récentes dans Latest data et confirmation de active_available=1.',
          'Rapporter les commandes exécutées, validations et résultats sans inclure aucun secret.',
        ].map((rule, index) => <li key={rule} className="flex gap-3 rounded-xl border border-white/10 bg-black/20 p-4"><span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400/15 font-black text-amber-200">{index + 1}</span><span>{rule}</span></li>)}
      </ol>
    </Panel>
    <Panel title="Conditions d’arrêt obligatoire" icon={AlertTriangle} tone="rose">
      <Bullets items={[
        'Distribution ou version non supportée par ce guide.',
        'Hostname ambigu, déjà utilisé ou différent entre l’agent et Zabbix.',
        'Une ancienne PSK ou configuration TLS existe et son ownership n’est pas clair.',
        'La validation zabbix_agent2 -T échoue.',
        'TCP 10051 est inaccessible.',
        'La création de l’hôte ou la saisie privée de la PSK exige un accès que le bot ne possède pas.',
        'Plus d’un item app.hermes.openai.email existe sur le même hôte.',
        'Les montages /etc ou /usr ne peuvent pas être restaurés dans leur état original.',
        'Aucune donnée récente n’arrive dans Zabbix après le délai prévu.',
      ]} />
    </Panel>
  </div>;
}

export default function ZabbixAgent2InstallationModal({ isOpen, onClose }: ZabbixAgent2InstallationModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('vue');
  const dialogRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    setActiveTab('vue');
    const focusFrame = window.requestAnimationFrame(() => dialogRef.current?.focus());
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ));
        if (focusable.length === 0) {
          event.preventDefault();
          dialogRef.current.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (document.activeElement === dialogRef.current || !dialogRef.current.contains(document.activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedElement?.focus();
    };
  }, [isOpen, onClose]);

  const content = {
    vue: <VueTab />, installation: <InstallationTab />, tls: <TlsTab />, zabbix: <ZabbixTab />,
    hermes: <HermesTab />, validation: <ValidationTab />, depannage: <DepannageTab />, bot: <BotTab />,
  }[activeTab];

  return <AnimatePresence>{isOpen && (
    <motion.div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-sm md:p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={onClose} role="dialog" aria-modal="true" aria-labelledby="zabbix-modal-title">
      <motion.div ref={dialogRef} tabIndex={-1} className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-cyan-300/20 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 shadow-2xl outline-none" initial={{ y: 30, scale: 0.98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 20, scale: 0.98 }} onMouseDown={(event) => event.stopPropagation()}>
        <header className="flex items-start justify-between gap-4 border-b border-white/10 bg-black/20 px-5 py-5 md:px-7">
          <div className="flex gap-4"><div className="rounded-2xl bg-cyan-400/10 p-3"><Server className="h-7 w-7 text-cyan-300" /></div><div><p className="text-xs font-black uppercase tracking-[0.25em] text-cyan-300">Procédure interne BZ</p><h2 id="zabbix-modal-title" className="mt-1 text-2xl font-black text-white md:text-3xl">Installation Zabbix</h2><p className="mt-1 text-sm text-slate-300">Mode actif · Zabbix 7.4 · TLS-PSK unique par machine</p></div></div>
          <button type="button" onClick={onClose} className="rounded-xl border border-white/10 bg-white/5 p-2 text-slate-300 hover:bg-white/10 hover:text-white" aria-label="Fermer"><X className="h-5 w-5" /></button>
        </header>
        <nav role="tablist" className="flex gap-2 overflow-x-auto border-b border-white/10 px-4 py-3 md:px-7" aria-label="Sections de la procédure">
          {tabs.map(({ id, label, icon: Icon }) => <button key={id} id={`zabbix-tab-${id}`} role="tab" aria-selected={activeTab === id} aria-controls={`zabbix-panel-${id}`} type="button" onClick={() => setActiveTab(id)} className={`flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${activeTab === id ? 'bg-cyan-400 text-slate-950' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}><Icon className="h-4 w-4" />{label}</button>)}
        </nav>
        <div id={`zabbix-panel-${activeTab}`} role="tabpanel" aria-labelledby={`zabbix-tab-${activeTab}`} className="overflow-y-auto p-4 md:p-7">{content}</div>
      </motion.div>
    </motion.div>
  )}</AnimatePresence>;
}
